/**
 * CiaoMessage — Individual chat message
 * Clean, borderless, modern design with attachment rendering
 * Auto-collapses inline JSON blocks behind a toggle
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IoDocument, IoCodeSlash } from 'react-icons/io5';

interface AttachmentMeta {
  type: string;
  name: string;
  pages?: number;
  preview?: string;
}

interface CiaoMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  agentName?: string;
  isLatest?: boolean;
  isExpanded?: boolean;
  attachments?: AttachmentMeta[];
}

/**
 * Detect large JSON blocks in message content and split into
 * [humanText, jsonBlocks[]] so we can render JSON collapsed.
 */
function splitJsonBlocks(text: string): { clean: string; jsonBlocks: string[] } {
  // Match JSON objects/arrays that are 120+ chars (likely raw API responses)
  const jsonPattern = /(\{[\s\S]{120,?\}|\[[\s\S]{120,?\])/g;
  const jsonBlocks: string[] = [];
  let clean = text;

  // Find strings that look like large JSON blobs
  const candidates = text.match(jsonPattern) || [];
  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      // It's valid JSON — extract it
      jsonBlocks.push(JSON.stringify(parsed, null, 2));
      clean = clean.replace(candidate, '').replace(/\*\*Response:\*\*\s*/, '').trim();
    } catch {
      // Not valid JSON, leave it
    }
  }

  return { clean, jsonBlocks };
}

/** Collapsible JSON block component */
const JsonBlock: React.FC<{ json: string }> = ({ json }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 6 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 10px',
          borderRadius: 6,
          background: open ? 'rgba(99,102,241,0.12)' : 'rgba(0,0,0,0.04)',
          color: open ? '#6366f1' : 'var(--text-muted, #999)',
          fontSize: 10,
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        <IoCodeSlash size={11} />
        {open ? 'Ocultar JSON' : 'Ver JSON'}
      </button>
      {open && (
        <pre
          style={{
            marginTop: 6,
            padding: 10,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.04)',
            fontSize: 10,
            lineHeight: 1.5,
            color: 'var(--text-muted, #666)',
            overflow: 'auto',
            maxHeight: 200,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {json}
        </pre>
      )}
    </div>
  );
};

const CiaoMessage: React.FC<CiaoMessageProps> = ({ role, content, timestamp, agentName, isLatest, isExpanded, attachments }) => {
  const isUser = role === 'user';
  const time = new Date(timestamp).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });

  // For assistant messages, detect and collapse inline JSON blobs
  const { clean: displayContent, jsonBlocks } = !isUser ? splitJsonBlocks(content) : { clean: content, jsonBlocks: [] };

  return (
    <div
      className={isLatest ? 'ciao-animate-fade-in' : undefined}
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 10,
      }}
    >
      <div style={{ maxWidth: isExpanded ? '75%' : '88%' }}>
        {/* Attachment previews (shown above the bubble for user messages) */}
        {isUser && attachments && attachments.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginBottom: 4,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            {attachments.map((att, idx) =>
              att.type === 'image' && att.preview ? (
                <img
                  key={idx}
                  src={att.preview}
                  alt={att.name}
                  style={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: 10,
                    border: '2px solid rgba(252,117,86,0.3)',
                  }}
                />
              ) : (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    background: 'rgba(252,117,86,0.1)',
                    borderRadius: 8,
                    padding: '4px 8px',
                    fontSize: 11,
                    color: '#FC7556',
                  }}
                >
                  <IoDocument size={12} />
                  <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {att.name}
                  </span>
                  {att.pages ? <span style={{ opacity: 0.7 }}>({att.pages}p)</span> : null}
                </div>
              ),
            )}
          </div>
        )}

        {/* Bubble */}
        <div
          style={{
            padding: isExpanded ? '14px 20px' : '12px 16px',
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            fontSize: isExpanded ? 15 : 13.5,
            lineHeight: 1.65,
            letterSpacing: '0.01em',
            fontWeight: 400,
            ...(isUser
              ? {
                  background: '#FC7556',
                  color: '#fff',
                }
              : {
                  background: 'var(--card-color, #f8f8f8)',
                  color: 'var(--text-color, #333)',
                }),
          }}
        >
          {isUser ? (
            <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>
          ) : (
            <>
              <div className={`ciao-markdown${isExpanded ? ' ciao-markdown--fullscreen' : ''}`}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Strip bold markers — render as plain text
                    strong: ({ children }) => <>{children}</>,
                    table: ({ children, ...props }) => (
                      <div className="ciao-table-scroll">
                        <table {...props}>{children}</table>
                      </div>
                    ),
                  }}
                >
                  {displayContent}
                </ReactMarkdown>
              </div>
              {jsonBlocks.map((json, idx) => (
                <JsonBlock key={idx} json={json} />
              ))}
            </>
          )}
        </div>

        {/* Time + Expert badge */}
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-color-muted, #999)',
            marginTop: 3,
            textAlign: isUser ? 'right' : 'left',
            paddingLeft: isUser ? 0 : 4,
            paddingRight: isUser ? 4 : 0,
            opacity: 0.7,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            gap: 6,
          }}
        >
          {time}
          {!isUser && agentName && agentName !== 'Ciao' && agentName !== 'Sistema' && agentName !== 'system' && agentName !== 'router' && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                padding: '1px 6px',
                borderRadius: 8,
                background: 'rgba(252, 117, 86, 0.08)',
                color: '#FC7556',
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '0.02em',
              }}
            >
              {agentName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CiaoMessage;
